import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { fetchSenderDigest, performSenderBulkAction } from '@/api/senders';
import type { Email } from '@/types/email';
import type {
  ActiveSenderGroup,
  SenderGroupInfo,
  SenderGroupQuickAction,
} from '@/features/email-list/types';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import { mergeEmailCollections } from '@/lib/emailMerging';
import { useAccountStore } from './accountStore';
import { useAttachmentStore } from './attachmentStore';
import { useBulkOperationsStore } from './bulkOperationsStore';
import { useEmailStore } from './emailStore';
import { useFolderStore } from './folderStore';
import { removeEmailsFromStore, isRecentlyRemoved } from './emailStateHelpers';
import { shouldDeferMailboxReload } from './mailboxReloadGuard';
import { resolveSenderDescriptor, resolveSenderGroupContexts } from '@/lib/senderContexts';

export interface SenderDigestState {
  group: SenderGroupInfo;
  emails: Email[];
  totalMessages: number;
  unreadCount: number;
  /**
   * True once every context has been paged to its end (or a load-more page
   * produced no net-new messages). Stops the "load more" sentinel from
   * re-firing even if `emails.length < totalMessages` (which can happen when
   * the same message is counted in multiple mailboxes/contexts).
   */
  fullyLoaded?: boolean;
}

type SenderGroupActionState = 'archive' | 'delete' | 'block' | 'approve' | null;

const DIGEST_PAGE_SIZE = 50;
const DIGEST_INITIAL_LIMIT = DIGEST_PAGE_SIZE;
const DIGEST_CONTEXT_MIN_LIMIT = 10;
const SKIP_MAILBOX_RELOAD_WINDOW_MS = 4000;

type MailboxReloadRequest = {
  accountId: string;
  folderId: string;
  options: { reset?: boolean };
};

interface ActiveSenderHydrationState {
  requestId: number;
  expectedTotal: number | null;
  initialCount: number;
  startedAt: number;
}

interface PrefetchCacheEntry {
  emails: Email[];
  fetchedAt: number;
  totalMessages: number;
}

/**
 * How the inbox organizes its rows: 'flat' shows every email individually,
 * 'senders' collapses by sender, 'briefing' pins briefing items above a flat
 * list. Briefing rendering only applies in inbox folders — elsewhere the mode
 * behaves as 'flat' while the preference persists.
 */
export type InboxMode = 'flat' | 'senders';

interface SenderGroupingStoreState {
  inboxMode: InboxMode;
  /**
   * Derived mirror of `inboxMode === 'senders'` for the many prop-drilled
   * consumers. Written ONLY by setInboxMode (and the persist merge) — never
   * set it directly.
   */
  groupBySender: boolean;
  activeSenderGroup: ActiveSenderGroup | null;
  activeSenderGroupInfo: SenderGroupInfo | null;
  activeSenderHydration: ActiveSenderHydrationState | null;
  senderGroupLoadingMore: boolean;
  /**
   * Per-(account:folder) fetch limits for the active group's pagination
   * (N15): the old equal split capped the dominant account's limit below its
   * own tail, so unified load-more stalled with hasMore retiring early. Each
   * non-exhausted context now grows its OWN limit per page.
   */
  senderGroupContextPaging: Record<string, { limit: number; exhausted: boolean }>;
  senderDigest: SenderDigestState | null;
  senderDigestLoading: boolean;
  senderDigestLoadingMore: boolean;
  senderDigestVisible: boolean;
  pendingGroupActions: Record<string, SenderGroupActionState>;
  senderGroupLoadRequestId: number;
  senderDigestRequestId: number;
  senderGroupLoadAbortController: AbortController | null;
  senderDigestAbortController: AbortController | null;
  senderDigestLoadMoreAbortController: AbortController | null;
  pendingMailboxReload: MailboxReloadRequest | null;
  skipNextMailboxReloadFor: { accountId: string; folderId: string; expiresAt: number } | null;
  prefetchCache: Map<string, PrefetchCacheEntry>;
  prefetchInProgress: Set<string>;
  // Cache of accurate sender totals learned from API calls (unified view needs this
  // because loaded emails may only have counts from one account)
  senderTotalsCache: Map<string, number>;
  // Flag to prevent bootstrap from clearing sender group during URL restoration
  restoringFromUrl: boolean;
  clearSenderCaches: () => void;
  clearSenderFromCaches: (senderKey: string) => void;
  setInboxMode: (mode: InboxMode, options?: { reload?: boolean }) => Promise<void>;
  toggleGroupBySender: (enabled: boolean, options?: { reload?: boolean }) => Promise<void>;
  selectSenderGroup: (group: SenderGroupInfo) => Promise<void>;
  loadMoreSenderGroupEmails: () => Promise<void>;
  exitSenderGroup: (options?: { skipReload?: boolean }) => Promise<void>;
  showSenderDigest: (group: SenderGroupInfo, options?: { autoOpen?: boolean }) => Promise<void>;
  closeSenderDigest: (options?: { forEmailSelection?: boolean }) => void;
  loadMoreSenderDigest: () => Promise<void>;
  archiveSenderGroup: (group: SenderGroupInfo) => Promise<void>;
  deleteSenderGroup: (group: SenderGroupInfo) => Promise<void>;
  handleQuickAction: (group: SenderGroupInfo, action: SenderGroupQuickAction) => Promise<void>;
  handleDigestMessageVisible: (email: Email) => void;
  collectSenderEmailsForGroup: (group: SenderGroupInfo, options?: { limit?: number }) => Promise<Email[]>;
  flushPendingMailboxReload: () => Promise<void>;
  updateDigestEmail: (emailId: string, updates: Partial<Email>) => void;
  prefetchSenderGroup: (group: SenderGroupInfo) => void;
  /** Drop the given ids from the open digest (action engine + realtime). */
  pruneDigestEmails: (emailIds: string[]) => void;
  /** Flip digest cards' read state (action engine optimistic apply). */
  setDigestReadState: (emailIds: string[], read: boolean) => void;
  /** Restore digest cards to captured prior read state (partial failure). */
  restoreDigestReadState: (priorReadById: Map<string, boolean>) => void;
  /** Arm the reload-skip window + in-flight marker for a group bulk action. */
  beginGroupAction: (groupKey: string, type: Exclude<SenderGroupActionState, null>) => void;
  /** Clear the in-flight marker; drops the pending reload when none remain. */
  endGroupAction: (groupKey: string) => void;
  /**
   * THE canonical reset for account/folder switches and external resets:
   * aborts in-flight group/digest fetches, bumps the digest request id (so a
   * late response can't resurrect the old folder's digest), and clears ALL
   * group + digest state including `senderDigestVisible`.
   */
  resetSenderGroupingState: (reason?: string) => void;
}

const applyAttachmentState = (email: Email): Email => {
  if (!email.attachments?.length || !email.accountId) {
    return email;
  }
  const { downloadedByEmail } = useAttachmentStore.getState();
  const downloaded = downloadedByEmail[`${email.accountId}:${email.id}`];
  if (!downloaded?.size) {
    return email;
  }
  let changed = false;
  const attachments = email.attachments.map((attachment) => {
    if (downloaded.has(attachment.id) && !attachment.downloaded) {
      changed = true;
      return { ...attachment, downloaded: true };
    }
    return attachment;
  });
  return changed ? { ...email, attachments } : email;
};

const normalizeSenderEmail = (
  email: Email,
  groupTotal: number | null | undefined,
  contextTotal?: number | null,
): Email => {
  const senderMailboxCount = email.senderMailboxCount ?? groupTotal ?? contextTotal ?? null;
  const enriched = senderMailboxCount != null ? { ...email, senderMailboxCount } : email;
  return applyAttachmentState(enriched);
};

const resetEmailSelection = () => {
  useEmailStore.getState().clearSelection();
};

export const useSenderGroupingStore = create<SenderGroupingStoreState>()(
  conditionalDevtools(
    persist((set, get) => {
    const pruneDigestEmails = (emailIds: string[]) => {
      if (!emailIds.length) {
        return;
      }
      const idSet = new Set(emailIds);
      set((state) => {
        if (!state.senderDigest) {
          return {};
        }
        const filtered = state.senderDigest.emails.filter((email) => !idSet.has(email.id));
        if (filtered.length === state.senderDigest.emails.length) {
          return {};
        }
        if (filtered.length === 0) {
          // Every card is gone (sender archived/deleted, or the last card
          // removed) — close the pane instead of leaving it visible over a
          // null digest ("Digest unavailable"). Abort + bump the request id
          // so a late load-more page can't resurrect it.
          state.senderDigestLoadMoreAbortController?.abort();
          return {
            senderDigest: null,
            senderDigestVisible: false,
            senderDigestLoadingMore: false,
            senderDigestLoadMoreAbortController: null,
            senderDigestRequestId: state.senderDigestRequestId + 1,
          };
        }
        return {
          senderDigest: {
            ...state.senderDigest,
            emails: filtered,
            unreadCount: filtered.filter((email) => !email.read).length,
            totalMessages: Math.max(
              filtered.length,
              state.senderDigest.totalMessages - (state.senderDigest.emails.length - filtered.length)
            ),
          },
        };
      });
    };

    const updateDigestReadState = (emailIds: string[], read: boolean) => {
      if (!emailIds.length) {
        return;
      }
      const idSet = new Set(emailIds);
      set((state) => {
        if (!state.senderDigest) {
          return {};
        }
        const touched = state.senderDigest.emails.some((email) => idSet.has(email.id));
        if (!touched) {
          return {};
        }
        const emails = state.senderDigest.emails.map((email) =>
          idSet.has(email.id) ? { ...email, read } : email
        );
        return {
          senderDigest: {
            ...state.senderDigest,
            emails,
            unreadCount: emails.filter((email) => !email.read).length,
          },
        };
      });
    };

    // Restore each digest email to its captured prior read state. Used on a
    // partial mark-read/unread failure so we don't blanket-flip every id to
    // `!read` (which would corrupt messages already in the target state).
    const restoreDigestReadState = (priorReadById: Map<string, boolean>) => {
      if (priorReadById.size === 0) {
        return;
      }
      set((state) => {
        if (!state.senderDigest) {
          return {};
        }
        let touched = false;
        const emails = state.senderDigest.emails.map((email) => {
          if (!priorReadById.has(email.id)) {
            return email;
          }
          const prior = priorReadById.get(email.id)!;
          if (email.read === prior) {
            return email;
          }
          touched = true;
          return { ...email, read: prior };
        });
        if (!touched) {
          return {};
        }
        return {
          senderDigest: {
            ...state.senderDigest,
            emails,
            unreadCount: emails.filter((email) => !email.read).length,
          },
        };
      });
    };

    const updateDigestEmail = (emailId: string, updates: Partial<Email>) => {
      set((state) => {
        if (!state.senderDigest) {
          return {};
        }
        const idx = state.senderDigest.emails.findIndex((email) => email.id === emailId);
        if (idx === -1) {
          return {};
        }
        const emails = [...state.senderDigest.emails];
        emails[idx] = { ...emails[idx], ...updates };
        const needsUnreadRecount = 'read' in updates;
        return {
          senderDigest: {
            ...state.senderDigest,
            emails,
            unreadCount: needsUnreadRecount
              ? emails.filter((email) => !email.read).length
              : state.senderDigest.unreadCount,
          },
        };
      });
    };

    // Prefetch sender group emails on hover to reduce perceived latency
    // when the user clicks to enter the group
    const prefetchSenderGroup = (group: SenderGroupInfo) => {
      const state = get();
      const groupKey = group.key;

      // Skip if we're already inside a sender group (user already clicked)
      if (state.activeSenderGroup) {
        return;
      }

      // Skip if already prefetched (cache valid for 30 seconds)
      const cached = state.prefetchCache.get(groupKey);
      if (cached && Date.now() - cached.fetchedAt < 30000) {
        return;
      }

      // Skip if prefetch already in progress
      if (state.prefetchInProgress.has(groupKey)) {
        return;
      }

      // Check if we need to prefetch (same logic as selectSenderGroup)
      const totalMessages = group.totalMessages ?? group.emails.length;
      const needsPrefetch = group.emails.length < DIGEST_PAGE_SIZE && group.emails.length < totalMessages;
      if (!needsPrefetch) {
        return;
      }

      const descriptor = resolveSenderDescriptor(group);
      if (!descriptor) {
        return;
      }

      // ONE sender-context resolver (unified-aware fan-out + loaded-email and
      // single-account fallbacks) — same contexts selectSenderGroup will use.
      const contexts = resolveSenderGroupContexts(group);
      if (contexts.size === 0) {
        return;
      }

      // Mark as in progress (but don't trigger unnecessary re-renders by batching with other updates)
      const newInProgress = new Set(state.prefetchInProgress);
      newInProgress.add(groupKey);
      set({ prefetchInProgress: newInProgress });

      // Start prefetch in background (don't await)
      (async () => {
        try {
          const contextEntries = Array.from(contexts.values());
          const contextCount = contextEntries.length || 1;
          const contextLimit = Math.max(
            DIGEST_CONTEXT_MIN_LIMIT,
            Math.ceil(DIGEST_PAGE_SIZE / contextCount),
          );

          const slices = await Promise.all(
            contextEntries.map(async (context) => {
              const result = await fetchSenderDigest({
                accountId: context.accountId,
                folderId: context.folderId,
                sender: descriptor,
                limit: contextLimit,
              });
              return result;
            })
          );

          // Check if user already entered a sender group while we were fetching
          // If so, don't update state to avoid interfering with the active group
          const currentState = get();
          if (currentState.activeSenderGroup) {
            if (import.meta.env.DEV) {
              console.log('[prefetchSenderGroup] Skipping cache update - user already entered group');
            }
            return;
          }

          const incoming: Email[] = [];
          // Sum totalMessages from all slices to get accurate cross-account total
          let apiTotalMessages = 0;
          slices.forEach((slice) => {
            apiTotalMessages += slice.totalMessages;
            slice.emails.forEach((email) => {
              incoming.push(normalizeSenderEmail(email, group.totalMessages, slice.totalMessages));
            });
          });

          const merged = mergeEmailCollections(group.emails, incoming);

          // Display the authoritative server message count (the same value the inbox
          // shows via senderMailboxCount). merged.length is a THREAD count (post
          // thread-collapse) and must NOT be used as the message total — doing so
          // shrank the badge after visiting a group and diverged from the inbox/digest.
          const actualTotal = apiTotalMessages > 0 ? apiTotalMessages : merged.length;

          // Store in cache
          const newCache = new Map(get().prefetchCache);
          newCache.set(groupKey, {
            emails: merged,
            fetchedAt: Date.now(),
            totalMessages: actualTotal,
          });

          // Also store accurate total in senderTotalsCache for list display
          const newTotalsCache = new Map(get().senderTotalsCache);
          newTotalsCache.set(groupKey, actualTotal);

          // Clean up old cache entries (older than 60 seconds)
          const now = Date.now();
          for (const [key, entry] of newCache.entries()) {
            if (now - entry.fetchedAt > 60000) {
              newCache.delete(key);
            }
          }

          set({ prefetchCache: newCache, senderTotalsCache: newTotalsCache });

          if (import.meta.env.DEV) {
            console.log('[prefetchSenderGroup] Prefetched emails for', groupKey, {
              count: merged.length,
              actualTotal,
            });
          }
        } catch (error) {
          // Silently ignore prefetch errors - this is a background optimization
          if (import.meta.env.DEV) {
            console.warn('[prefetchSenderGroup] Failed to prefetch', groupKey, error);
          }
        } finally {
          // Only update prefetchInProgress if we're not in a sender group
          // to avoid state updates that could interfere with the active group
          const currentState = get();
          if (!currentState.activeSenderGroup) {
            const current = currentState.prefetchInProgress;
            const updated = new Set(current);
            updated.delete(groupKey);
            set({ prefetchInProgress: updated });
          }
        }
      })();
    };

    return {
    inboxMode: 'senders' as InboxMode,
    groupBySender: true,
    activeSenderGroup: null,
    activeSenderGroupInfo: null,
    activeSenderHydration: null,
    senderGroupLoadingMore: false,
    senderGroupContextPaging: {},
    senderDigest: null,
    senderDigestLoading: false,
    senderDigestLoadingMore: false,
    senderDigestVisible: false,
    pendingGroupActions: {},
    senderGroupLoadRequestId: 0,
    senderDigestRequestId: 0,
    senderGroupLoadAbortController: null,
    senderDigestAbortController: null,
    senderDigestLoadMoreAbortController: null,
    pendingMailboxReload: null,
    skipNextMailboxReloadFor: null,
    prefetchCache: new Map(),
    prefetchInProgress: new Set(),
    senderTotalsCache: new Map(),
    restoringFromUrl: false,

    clearSenderCaches: () => {
      set({ senderTotalsCache: new Map(), prefetchCache: new Map() });
    },

    clearSenderFromCaches: (senderKey: string) => {
      const { senderTotalsCache, prefetchCache } = get();
      const newTotals = new Map(senderTotalsCache);
      const newPrefetch = new Map(prefetchCache);
      newTotals.delete(senderKey);
      newPrefetch.delete(senderKey);
      set({ senderTotalsCache: newTotals, prefetchCache: newPrefetch });
    },

    setInboxMode: async (mode, options = {}) => {
      const reload = options.reload !== false;
      const accountId = useAccountStore.getState().selectedAccountId;
      const folderId = useFolderStore.getState().selectedFolderId();
      const bulkStore = useBulkOperationsStore.getState();
      bulkStore.setBulkEditMode(false);
      const enabled = mode === 'senders';
      set((state) => ({
        inboxMode: mode,
        groupBySender: enabled,
        activeSenderGroup: null,
        activeSenderGroupInfo: null,
        activeSenderHydration: null,
        senderGroupLoadingMore: false,
        senderGroupContextPaging: {},
        senderDigest: null,
        senderDigestLoading: false,
        senderDigestLoadingMore: false,
        // A stale digest pane must not survive the grouping toggle, and a late
        // digest response must not resurrect it (request id bumped).
        senderDigestVisible: false,
        senderDigestRequestId: state.senderDigestRequestId + 1,
        pendingGroupActions: {},
        skipNextMailboxReloadFor: null,
        // Clear caches when disabling sender grouping to free memory
        ...(!enabled ? { senderTotalsCache: new Map(), prefetchCache: new Map(), prefetchInProgress: new Set() } : {}),
      }));
      // Any briefing drill-down scope belongs to the previous mode; the reload
      // below restores the mailbox list, so a plain scope clear is enough.
      const { useBriefingStore } = await import('./briefingStore');
      useBriefingStore.setState({ scope: null });
      if (!enabled) {
        get().senderGroupLoadAbortController?.abort();
        get().senderDigestAbortController?.abort();
        resetEmailSelection();
      } else {
        resetEmailSelection();
      }
      if (accountId && folderId) {
        const emailStore = useEmailStore.getState();
        const shouldReload = reload || emailStore.emails.length === 0;
        if (shouldReload) {
          await emailStore.loadEmails(accountId, folderId, {
            reset: true,
          });
        } else {
          await get().flushPendingMailboxReload();
        }
      } else if (!reload) {
        await get().flushPendingMailboxReload();
      }
    },

    toggleGroupBySender: async (enabled, options = {}) =>
      get().setInboxMode(enabled ? 'senders' : 'flat', options),

    selectSenderGroup: async (group) => {
      if (import.meta.env.DEV) {
        console.log('[selectSenderGroup] Starting:', {
          key: group.key,
          emailCount: group.emails.length,
          totalMessages: group.totalMessages,
        });
      }

      get().senderGroupLoadAbortController?.abort();

      const hasAuthoritativeTotal = typeof group.totalMessages === 'number' && Number.isFinite(group.totalMessages);
      const totalMessages = hasAuthoritativeTotal
        ? Math.max(group.totalMessages!, group.emails.length)
        : group.emails.length;

      // Set up the active sender group state
      set({
        activeSenderGroup: {
          key: group.key,
          contactId: group.contactId ?? null,
          email: group.senderEmail ?? null,
          name: group.senderName ?? null,
        },
        activeSenderGroupInfo: group,
        activeSenderHydration: null,
        senderGroupLoadingMore: false,
        senderGroupContextPaging: {},
        senderDigest: null,
        senderDigestVisible: false,
        senderGroupLoadAbortController: null,
      });

      resetEmailSelection();
      useBulkOperationsStore.getState().setBulkEditMode(false);

      // Clear any in-progress prefetch state for this group to avoid race conditions
      const currentPrefetchInProgress = get().prefetchInProgress;
      if (currentPrefetchInProgress.has(group.key)) {
        const updatedInProgress = new Set(currentPrefetchInProgress);
        updatedInProgress.delete(group.key);
        set({ prefetchInProgress: updatedInProgress });
      }

      // In unified view, always fetch from sender digest to get complete emails from all accounts.
      // The group's emails only contain what's loaded in the current view, which may not include
      // all emails from the sender across all accounts.
      const selectedAccountId = useAccountStore.getState().selectedAccountId;
      const isUnifiedView = selectedAccountId === UNIFIED_ACCOUNT_ID;

      // Check if we have prefetched data for this group. The prefetch resolves
      // contexts through the same unified-aware fan-out as this function (C5),
      // so its result is complete in the unified view too — re-fetching here
      // just duplicated the hover's work (N26).
      const cached = get().prefetchCache.get(group.key);
      const cacheValid = cached && Date.now() - cached.fetchedAt < 30000;

      // If we have enough emails already (or cached), show them immediately
      // Otherwise, fetch a proper first page. Without an authoritative cached
      // page, the unified view always fetches (loaded rows may cover only one
      // account).
      const needsInitialFetch = !cacheValid && (
        isUnifiedView ||
        group.emails.length < DIGEST_PAGE_SIZE && group.emails.length < totalMessages
      );

      if (cacheValid) {
        // Use prefetched data
        const prefetchedEmails = cached.emails;
        const hasMore = prefetchedEmails.length < cached.totalMessages;

        // Clear the cache entry since we've used it, and update activeSenderGroupInfo
        // with the correct totalMessages from the cache to prevent load-more loops
        const newCache = new Map(get().prefetchCache);
        newCache.delete(group.key);
        set({
          prefetchCache: newCache,
          pendingMailboxReload: null,
          // Update activeSenderGroupInfo with cached totalMessages
          activeSenderGroupInfo: {
            ...group,
            totalMessages: cached.totalMessages,
          },
        });

        useEmailStore.getState().invalidateLoadRequests();
        // Single-writer path: builds the index, re-asserts the optimistic flag
        // overlay, and projects through the suppression-filtered projection.
        useEmailStore.getState().replaceList(prefetchedEmails, {
          source: { kind: 'senderGroup', groupKey: group.key },
          hasMore,
          loading: false,
          initialLoading: false,
        });

        if (import.meta.env.DEV) {
          console.log('[selectSenderGroup] Used prefetched data:', {
            emailCount: prefetchedEmails.length,
            totalMessages: cached.totalMessages,
            hasMore,
          });
        }
        return;
      }

      if (needsInitialFetch) {
        // Show loading state with whatever we have
        const initialEmails = group.emails.map((email) => normalizeSenderEmail(email, group.totalMessages));

        useEmailStore.getState().invalidateLoadRequests();
        useEmailStore.getState().replaceList(initialEmails, {
          source: { kind: 'senderGroup', groupKey: group.key },
          hasMore: true,
          loading: true,
          initialLoading: false,
        });

        // Fetch first page
        const descriptor = resolveSenderDescriptor(group);
        if (!descriptor) {
          useEmailStore.setState({ emailsLoading: false });
          return;
        }

        // ONE sender-context resolver (unified-aware fan-out across all
        // accounts; loaded-emails + single-account fallback otherwise).
        const contexts = resolveSenderGroupContexts(group);

        if (import.meta.env.DEV) {
          console.log('[selectSenderGroup] Final contexts:', {
            size: contexts.size,
            contexts: Array.from(contexts.entries()),
          });
        }

        if (!contexts.size) {
          useEmailStore.setState({ emailsLoading: false });
          return;
        }

        const controller = new AbortController();
        set({ senderGroupLoadAbortController: controller });

        try {
          const contextEntries = Array.from(contexts.entries());
          const contextCount = contextEntries.length || 1;
          const contextLimit = Math.max(
            DIGEST_CONTEXT_MIN_LIMIT,
            Math.ceil(DIGEST_PAGE_SIZE / contextCount),
          );

          const slices = await Promise.all(
            contextEntries.map(async ([key, context]) => {
              const result = await fetchSenderDigest({
                accountId: context.accountId,
                folderId: context.folderId,
                sender: descriptor,
                limit: contextLimit,
                signal: controller.signal,
              });
              return { key, result };
            })
          );

          const incoming: Email[] = [];
          // Track if any context has more emails to fetch and sum up totalMessages
          let anyContextHasMore = false;
          let apiTotalMessages = 0;
          // Seed per-context paging (N15) so load-more grows each context's
          // OWN limit instead of equal-splitting across accounts.
          const paging: Record<string, { limit: number; exhausted: boolean }> = {};
          slices.forEach(({ key, result: slice }) => {
            const exhausted = slice.emails.length >= slice.totalMessages;
            paging[key] = { limit: contextLimit, exhausted };
            if (!exhausted) {
              anyContextHasMore = true;
            }
            apiTotalMessages += slice.totalMessages;
            slice.emails.forEach((email) => {
              incoming.push(normalizeSenderEmail(email, group.totalMessages, slice.totalMessages));
            });
          });

          const merged = mergeEmailCollections(
            initialEmails,
            incoming
          );

          // Determine hasMore based on whether we got new emails and any context has more
          const gotNewEmails = merged.length > initialEmails.length;
          const hasMore = gotNewEmails && anyContextHasMore;
          // Display the authoritative server message count (matches the inbox's
          // senderMailboxCount). Never use merged.length — that is a THREAD count
          // and would make the badge shrink after visiting, diverging from the
          // inbox/digest counts. Pagination still stops via `hasMore` above.
          const actualTotal = apiTotalMessages > 0 ? apiTotalMessages : merged.length;

          // Validity check: a late response must not overwrite the list if the
          // user has exited the group or entered a different one meanwhile.
          if (get().activeSenderGroup?.key !== group.key) {
            return;
          }

          // Clear any pending mailbox reload BEFORE setting emailsLoading: false
          // because the bootstrap subscription fires synchronously on state change
          // Also update activeSenderGroupInfo with actual totalMessages to prevent load-more loops
          // Update senderTotalsCache with accurate total for list display
          const newTotalsCache = new Map(get().senderTotalsCache);
          newTotalsCache.set(group.key, actualTotal);

          set((current) => ({
            pendingMailboxReload: null,
            senderTotalsCache: newTotalsCache,
            senderGroupContextPaging: paging,
            activeSenderGroupInfo: current.activeSenderGroupInfo
              ? { ...current.activeSenderGroupInfo, totalMessages: actualTotal }
              : null,
          }));

          // Single-writer path (overlay + suppression-filtered projection inside)
          useEmailStore.getState().replaceList(merged, {
            source: { kind: 'senderGroup', groupKey: group.key },
            hasMore,
            loading: false,
          });

          if (import.meta.env.DEV) {
            console.log('[selectSenderGroup] Fetched first page:', {
              before: initialEmails.length,
              after: merged.length,
              apiTotalMessages,
              actualTotal,
              anyContextHasMore,
              hasMore,
            });
          }
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            // Aborted because a NEWER group/digest request took over — that
            // request owns emailsLoading now; clearing it here would strip the
            // next group's loading state (A15).
            return;
          }
          console.error('Failed to load initial sender group emails', error);
          useEmailStore.setState({ emailsLoading: false });
        } finally {
          set((current) => {
            if (current.senderGroupLoadAbortController === controller) {
              return { senderGroupLoadAbortController: null };
            }
            return {};
          });
        }
      } else {
        // We have enough emails, just show them
        const initialEmails = group.emails.map((email) => normalizeSenderEmail(email, group.totalMessages));
        const hasMore = group.emails.length < totalMessages;

        // Clear any pending mailbox reload BEFORE setting emailsLoading: false
        // because the bootstrap subscription fires synchronously on state change
        set({ pendingMailboxReload: null });

        useEmailStore.getState().invalidateLoadRequests();
        useEmailStore.getState().replaceList(initialEmails, {
          source: { kind: 'senderGroup', groupKey: group.key },
          hasMore,
          loading: false,
          initialLoading: false,
        });

        if (import.meta.env.DEV) {
          console.log('[selectSenderGroup] Initialized with existing emails:', {
            emailCount: initialEmails.length,
            totalMessages,
            hasMore,
          });
        }
      }
    },

    loadMoreSenderGroupEmails: async () => {
      const state = get();
      if (state.senderGroupLoadingMore || !state.activeSenderGroupInfo) {
        return;
      }

      const group = state.activeSenderGroupInfo;
      const descriptor = resolveSenderDescriptor(group);
      if (!descriptor) {
        return;
      }

      const contexts = resolveSenderGroupContexts(group);
      if (!contexts.size) {
        return;
      }

      const currentEmails = useEmailStore.getState().emails;
      const hasAuthoritativeTotal = typeof group.totalMessages === 'number' && Number.isFinite(group.totalMessages);
      const totalMessages = hasAuthoritativeTotal
        ? Math.max(group.totalMessages!, currentEmails.length)
        : currentEmails.length;

      if (currentEmails.length >= totalMessages) {
        useEmailStore.setState({ hasMoreEmails: false });
        return;
      }

      get().senderGroupLoadAbortController?.abort();
      const controller = new AbortController();
      set({
        senderGroupLoadingMore: true,
        senderGroupLoadAbortController: controller,
      });

      try {
        const contextEntries = Array.from(contexts.entries());
        const paging = { ...get().senderGroupContextPaging };
        const activeContexts = contextEntries.filter(([key]) => !paging[key]?.exhausted);
        const activeCount = activeContexts.length || 1;
        // Each NON-EXHAUSTED context grows its OWN limit by its page share
        // (N15): the old equal split over ALL contexts capped the dominant
        // account's limit below its own tail, stalling unified load-more.
        const pageShare = Math.max(
          DIGEST_CONTEXT_MIN_LIMIT,
          Math.ceil(DIGEST_PAGE_SIZE / activeCount),
        );

        if (import.meta.env.DEV) {
          console.log('[loadMoreSenderGroupEmails] Fetching:', {
            currentCount: currentEmails.length,
            activeContexts: activeContexts.length,
            pageShare,
          });
        }

        const slices = await Promise.all(
          contextEntries.map(async ([key, context]) => {
            const entry = paging[key];
            if (entry?.exhausted) {
              return null;
            }
            // Unseeded context (e.g. the "enough emails already" entry path
            // skipped the initial fetch): base the limit on the loaded list
            // length so the fetch reaches past everything already shown.
            const baseLimit = entry?.limit ?? currentEmails.length;
            const limit = baseLimit + pageShare;
            const result = await fetchSenderDigest({
              accountId: context.accountId,
              folderId: context.folderId,
              sender: descriptor,
              limit,
              signal: controller.signal,
            });
            paging[key] = {
              limit,
              exhausted: result.emails.length >= result.totalMessages,
            };
            return result;
          })
        );

        const incoming: Email[] = [];
        // Track if any context has more emails to fetch and sum up totalMessages
        let anyContextHasMore = false;
        let apiTotalMessages = 0;
        slices.forEach((slice) => {
          if (!slice) {
            return;
          }
          if (slice.emails.length < slice.totalMessages) {
            anyContextHasMore = true;
          }
          apiTotalMessages += slice.totalMessages;
          slice.emails.forEach((email) => {
            incoming.push(normalizeSenderEmail(email, group.totalMessages, slice.totalMessages));
          });
        });
        set({ senderGroupContextPaging: paging });

        const merged = mergeEmailCollections(
          currentEmails,
          incoming
        );

        // Determine hasMore based on whether we got new emails and any context has more
        const gotNewEmails = merged.length > currentEmails.length;
        const hasMore = gotNewEmails && anyContextHasMore;
        // Display the authoritative server message count (matches the inbox's
        // senderMailboxCount). Never use merged.length — that is a THREAD count
        // and would make the badge shrink after visiting, diverging from the
        // inbox/digest counts. Pagination still stops via `hasMore` above.
        // Exhausted contexts are no longer re-fetched (N15), so the page sum
        // can undercount — the previously-established total is the floor.
        const previousTotal =
          typeof group.totalMessages === 'number' && Number.isFinite(group.totalMessages)
            ? group.totalMessages
            : 0;
        const actualTotal = Math.max(
          apiTotalMessages > 0 ? apiTotalMessages : merged.length,
          previousTotal
        );

        if (import.meta.env.DEV) {
          console.log('[loadMoreSenderGroupEmails] Merged:', {
            before: currentEmails.length,
            after: merged.length,
            apiTotalMessages,
            actualTotal,
            anyContextHasMore,
            gotNewEmails,
            hasMore,
          });
        }

        // Validity check: a late load-more must not overwrite the list after the
        // user exited the group or entered a different one.
        if (get().activeSenderGroup?.key !== group.key) {
          return;
        }

        // Update activeSenderGroupInfo with actual totalMessages to prevent further load-more loops
        // Also update senderTotalsCache for list display
        const newTotalsCache = new Map(get().senderTotalsCache);
        if (group) {
          newTotalsCache.set(group.key, actualTotal);
        }

        set((current) => ({
          senderTotalsCache: newTotalsCache,
          activeSenderGroupInfo: current.activeSenderGroupInfo
            ? { ...current.activeSenderGroupInfo, totalMessages: actualTotal }
            : null,
        }));

        // Single-writer path (overlay + suppression-filtered projection inside)
        useEmailStore.getState().replaceList(merged, {
          source: { kind: 'senderGroup', groupKey: group.key },
          hasMore,
        });
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('Failed to load more sender group emails', error);
          // Stop the load-more trigger from re-firing on every failed fetch.
          // Without this, a failing /senders/digest call leaves hasMoreEmails
          // true, so the scroll observer immediately retries — an unbounded
          // retry storm (hundreds of calls). Fail cleanly; re-entering the
          // group resets hasMoreEmails and retries.
          useEmailStore.setState({ hasMoreEmails: false });
        }
      } finally {
        set((current) => {
          if (current.senderGroupLoadAbortController === controller) {
            return {
              senderGroupLoadAbortController: null,
              senderGroupLoadingMore: false,
            };
          }
          return { senderGroupLoadingMore: false };
        });
      }
    },

    exitSenderGroup: async (options) => {
      get().senderGroupLoadAbortController?.abort();
      get().senderDigestAbortController?.abort();
      get().senderDigestLoadMoreAbortController?.abort();
      set({
        activeSenderGroup: null,
        activeSenderGroupInfo: null,
        activeSenderHydration: null,
        senderGroupLoadingMore: false,
        senderGroupContextPaging: {},
        senderDigest: null,
        senderDigestLoading: false,
        senderDigestLoadingMore: false,
        senderDigestVisible: false,
        senderDigestLoadMoreAbortController: null,
        skipNextMailboxReloadFor: null,
        // Clear sender caches so fresh totals are used after reload
        senderTotalsCache: new Map(),
        prefetchCache: new Map(),
      });
      resetEmailSelection();
      const accountId = useAccountStore.getState().selectedAccountId;
      const folderId = useFolderStore.getState().selectedFolderId();
      if (accountId && folderId) {
        const reloadRequest = {
          accountId,
          folderId,
          options: {
            reset: true,
          },
        };
        if (options?.skipReload) {
          set({ pendingMailboxReload: reloadRequest });
        } else {
          await useEmailStore.getState().loadEmails(accountId, folderId, reloadRequest.options);
          await get().flushPendingMailboxReload();
        }
      }
    },

    showSenderDigest: async (group, options = {}) => {
      const { autoOpen = true } = options;

      if (get().pendingGroupActions[group.key]) {
        return;
      }

      // A fetch already in flight is ABORTED and replaced below (request id +
      // abort controller) — silently dropping the user's newer intent meant a
      // URL-restore could permanently beat the header button (C14).

      if (import.meta.env.DEV) {
        console.log('[showSenderDigest] Starting:', {
          key: group.key,
          groupEmailCount: group.emails.length,
          totalMessages: group.totalMessages,
        });
      }
      const descriptor = resolveSenderDescriptor(group);
      if (!descriptor) {
        return;
      }
      const requestId = get().senderDigestRequestId + 1;
      get().senderDigestLoadMoreAbortController?.abort();
      get().senderDigestAbortController?.abort();
      const controller = new AbortController();
      set((state) => ({
        senderDigestRequestId: requestId,
        senderDigestAbortController: controller,
        senderDigestLoading: autoOpen,
        senderDigestLoadingMore: false,
        senderDigestLoadMoreAbortController: null,
        senderDigest: autoOpen ? null : state.senderDigest,
        senderDigestVisible: autoOpen ? true : state.senderDigestVisible,
      }));
      if (autoOpen) {
        resetEmailSelection();
        // Hover hygiene (N20): the digest covers the list — a keyboard action
        // must not fall through to the group row still hovered behind it.
        const { useHoverStore } = await import('./hoverStore');
        useHoverStore.getState().clearHover();
      }

      // A newer showSenderDigest may have bumped the request id during the
      // autoOpen hover-clear await above — bail before writing digest state for
      // a superseded request (X13: wrong-sender digest flash).
      if (get().senderDigestRequestId !== requestId) {
        return;
      }

      const contexts = resolveSenderGroupContexts(group);
      if (!contexts.size) {
        const unreadCount = group.emails.filter((email) => !email.read).length;
        set({
          senderDigestAbortController: null,
          senderDigest: {
            group,
            emails: group.emails,
            totalMessages: group.totalMessages ?? group.emails.length,
            unreadCount,
            // No contexts to page against, so this is everything we can show.
            fullyLoaded: true,
          },
          senderDigestLoading: false,
          senderDigestLoadingMore: false,
        });
        return;
      }

      const contextEntries = Array.from(contexts.values());
      const contextCount = contextEntries.length;
      if (contextCount === 0) {
        set({ senderDigestAbortController: null, senderDigestLoading: false, senderDigestLoadingMore: false });
        return;
      }

      try {
        const initialLimitCandidate =
          typeof group.totalMessages === 'number' && group.totalMessages > 0
            ? Math.min(DIGEST_INITIAL_LIMIT, group.totalMessages)
            : DIGEST_INITIAL_LIMIT;
        const initialLimit = Math.max(1, initialLimitCandidate);

        const contextLimit = Math.max(
          DIGEST_CONTEXT_MIN_LIMIT,
          Math.ceil(initialLimit / contextCount),
        );

        const slices = await Promise.all(
          contextEntries.map(async (context) => {
            const result = await fetchSenderDigest({
              accountId: context.accountId,
              folderId: context.folderId,
              sender: descriptor,
              limit: contextLimit,
              includeBodies: true,
              signal: controller.signal,
            });
            return result;
          })
        );

        if (get().senderDigestRequestId !== requestId) {
          return;
        }

        const emailMap = new Map<string, Email>();
        let totalMessages = 0;
        slices.forEach((slice) => {
          totalMessages += slice.totalMessages;
          slice.emails.forEach((email) => {
            // Use composite key to ensure uniqueness across accounts
            const key = `${email.accountId ?? 'unknown'}:${email.id}`;
            if (!emailMap.has(key)) {
              emailMap.set(key, applyAttachmentState(email));
            }
          });
        });
        // Keep one row per message (no thread collapse): the digest renders an
        // individual card per message and totalMessages is message-level, so
        // collapsing would hide messages and break load-more termination.
        const merged = mergeEmailCollections(
          [],
          Array.from(emailMap.values()),
          { disableThreading: true }
        );

        const resolvedTotal = totalMessages || group.totalMessages || merged.length;

        if (import.meta.env.DEV) {
          console.log('[showSenderDigest] Digest loaded:', {
            totalMessages,
            fetchedEmails: emailMap.size,
            mergedEmails: merged.length,
          });
        }

        set({
          senderDigest: {
            group,
            emails: merged,
            totalMessages: resolvedTotal,
            unreadCount: merged.filter((email) => !email.read).length,
            fullyLoaded: merged.length >= resolvedTotal,
          },
          senderDigestLoading: false,
          senderDigestLoadingMore: false,
          ...(get().senderDigestAbortController === controller ? { senderDigestAbortController: null } : {}),
        });
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('Failed to load sender digest', error);
          set({
            senderDigest: null,
            senderDigestLoadingMore: false,
            ...(get().senderDigestAbortController === controller ? { senderDigestAbortController: null, senderDigestLoading: false } : {}),
          });
        } else if (get().senderDigestAbortController === controller) {
          set({ senderDigestAbortController: null, senderDigestLoading: false, senderDigestLoadingMore: false });
        }
      }
    },

    closeSenderDigest: (options) => {
      const state = get();
      const digestGroup = state.senderDigest?.group;
      const hasActiveSenderGroup = state.activeSenderGroup !== null;
      const wasDigestVisible = state.senderDigestVisible;
      const forEmailSelection = options?.forEmailSelection ?? false;

      get().senderDigestAbortController?.abort();
      get().senderDigestLoadMoreAbortController?.abort();
      set({
        senderDigest: null,
        senderDigestLoading: false,
        senderDigestLoadingMore: false,
        senderDigestAbortController: null,
        senderDigestLoadMoreAbortController: null,
        senderDigestVisible: false,
        senderDigestRequestId: get().senderDigestRequestId + 1,
      });

      // Skip re-selecting sender group when closing digest for email selection.
      // selectSenderGroup calls resetEmailSelection which would clear the email
      // that's about to be selected, causing an empty detail view.
      if (forEmailSelection) {
        return;
      }

      // Only re-select the sender group if the digest was actually visible
      // (not just prefetched). This prevents clearing email selection when
      // clicking an email while in a sender group without the digest open.
      if (hasActiveSenderGroup && digestGroup && wasDigestVisible) {
        void get().selectSenderGroup(digestGroup);
      } else if (!hasActiveSenderGroup) {
        // Only flush pending mailbox reload when NOT in a sender group.
        // If we're in a sender group, don't flush - we want to stay in the group.
        void get().flushPendingMailboxReload();
      }
      // If hasActiveSenderGroup is true but digest wasn't visible, do nothing -
      // the user is just clicking an email within the sender group.
    },

    loadMoreSenderDigest: async () => {
      const state = get();
      if (state.senderDigestLoadingMore || state.senderDigestLoading) {
        return;
      }
      const digest = state.senderDigest;
      if (!digest) {
        return;
      }
      if (digest.fullyLoaded || digest.emails.length >= digest.totalMessages) {
        return;
      }
      const descriptor = resolveSenderDescriptor(digest.group);
      if (!descriptor) {
        return;
      }
      const folderId = useFolderStore.getState().selectedFolderId();
      const contexts = resolveSenderGroupContexts(digest.group);
      if (!contexts.size) {
        return;
      }
      get().senderDigestLoadMoreAbortController?.abort();
      const controller = new AbortController();
      set({
        senderDigestLoadMoreAbortController: controller,
        senderDigestLoadingMore: true,
      });
      try {
        const contextEntries = Array.from(contexts.entries());
        const contextCount = contextEntries.length || 1;
        const perContextLimit = Math.max(
          DIGEST_CONTEXT_MIN_LIMIT,
          Math.ceil(DIGEST_PAGE_SIZE / contextCount),
        );

        // Threading is disabled for the digest, so digest.emails holds one row
        // per message. The number already loaded for a context is exactly that
        // context's pagination offset — fetch only the *next* page rather than
        // re-downloading the whole prefix (with bodies) every time.
        const loadedByContext = new Map<string, number>();
        digest.emails.forEach((email) => {
          const emailFolderId = email.folderId ?? folderId ?? null;
          if (!email.accountId || !emailFolderId) {
            return;
          }
          const key = `${email.accountId}:${emailFolderId}`;
          loadedByContext.set(key, (loadedByContext.get(key) ?? 0) + 1);
        });

        const slices = await Promise.all(
          contextEntries.map(async ([key, context]) => {
            const result = await fetchSenderDigest({
              accountId: context.accountId,
              folderId: context.folderId,
              sender: descriptor,
              limit: perContextLimit,
              offset: loadedByContext.get(key) ?? 0,
              includeBodies: true,
              signal: controller.signal,
            });
            return result;
          })
        );

        const incoming: Email[] = [];
        slices.forEach((slice) => {
          slice.emails.forEach((email) => {
            incoming.push(applyAttachmentState(email));
          });
        });

        if (!incoming.length) {
          // Every context's offset reached the end — nothing more to load.
          set((current) => ({
            senderDigest: current.senderDigest
              ? { ...current.senderDigest, fullyLoaded: true }
              : current.senderDigest,
          }));
          return;
        }

        set((current) => {
          if (!current.senderDigest) {
            return {};
          }
          // Merge against the CURRENT digest (not the pre-await `digest`
          // snapshot) and drop any id pruned/suppressed while the page was in
          // flight (X5) — otherwise a card archived mid-fetch resurrects and
          // re-inflates the counts. mergeEmailCollections dedupes by identity.
          const base = current.senderDigest.emails.filter(
            (email) => !isRecentlyRemoved(email.id, email.folderId)
          );
          const freshIncoming = incoming.filter(
            (email) => !isRecentlyRemoved(email.id, email.folderId)
          );
          const merged = mergeEmailCollections(base, freshIncoming, {
            disableThreading: true,
          });
          // A page with results but no net-new distinct messages means we can't
          // make further progress (e.g. a message counted in two contexts) —
          // stop here so the load-more sentinel retires. Preserve the
          // authoritative total established at initial load; a partial page
          // (exhausted contexts report total 0) must never shrink it.
          const madeProgress = merged.length > current.senderDigest.emails.length;
          const unreadCount = merged.filter((email) => !email.read).length;
          return {
            senderDigest: {
              ...current.senderDigest,
              emails: merged,
              unreadCount,
              fullyLoaded: !madeProgress || merged.length >= current.senderDigest.totalMessages,
            },
          };
        });
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('Failed to load more sender digest emails', error);
          // Stop the load-more sentinel from re-firing on every failed fetch
          // (see loadMoreSenderGroupEmails for the same rationale).
          set((current) => ({
            senderDigest: current.senderDigest
              ? { ...current.senderDigest, fullyLoaded: true }
              : current.senderDigest,
          }));
        }
      } finally {
        set((current) => {
          if (current.senderDigestLoadMoreAbortController === controller) {
            return {
              senderDigestLoadMoreAbortController: null,
              senderDigestLoadingMore: false,
            };
          }
          return { senderDigestLoadingMore: false };
        });
      }
    },

    archiveSenderGroup: async (group) => {
      // The action engine owns the whole flow: unified-aware context fan-out,
      // optimistic removal (incl. thread members + digest cards), undo built
      // from the server's complete affected id set, restore-on-failure.
      const { executeSenderGroupAction } = await import('./emailCommands');
      await executeSenderGroupAction('archive', group);
    },

    deleteSenderGroup: async (group) => {
      const { executeSenderGroupAction } = await import('./emailCommands');
      await executeSenderGroupAction('delete', group);
    },

    handleQuickAction: async (group, action) => {
      switch (action) {
        case 'archive':
          await get().archiveSenderGroup(group);
          return;
        case 'delete':
          await get().deleteSenderGroup(group);
          return;
        case 'markRead':
        case 'markUnread': {
          const { executeSenderGroupAction } = await import('./emailCommands');
          await executeSenderGroupAction(action, group);
          return;
        }
        case 'blockSender':
        case 'approveSender': {
          // Contact-state changes keep their own dispatch (different domain than
          // mail moves) but adopt the engine's failure standard: double-fire
          // guard, optimistic removal, rollback + loud toast on failure.
          const groupKey = group.key;
          if (!groupKey || get().pendingGroupActions[groupKey]) {
            return;
          }
          const isBlock = action === 'blockSender';
          const descriptor = resolveSenderDescriptor(group);
          const contexts = resolveSenderGroupContexts(group);
          if (!descriptor || !contexts.size) {
            toast.error("Couldn't resolve this sender — no action taken");
            return;
          }

          // Collect ALL loaded messages from this sender (group rows are
          // thread reps; orphan members would otherwise resurface as a new row)
          const normalizedSender = (group.senderEmail || group.emails[0]?.from?.email || '')
            .toLowerCase().trim();
          const idSet = new Set(group.emails.map((e) => e.id).filter(Boolean));
          const snapshotById = new Map<string, Email>();
          group.emails.forEach((e) => e.id && snapshotById.set(e.id, e));
          if (normalizedSender) {
            const emailIndex = useEmailStore.getState().emailIndex;
            for (const email of Object.values(emailIndex)) {
              if (email?.from?.email?.toLowerCase().trim() === normalizedSender) {
                idSet.add(email.id);
                snapshotById.set(email.id, email);
              }
            }
          }
          const ids = Array.from(idSet);

          set((state) => ({
            pendingGroupActions: {
              ...state.pendingGroupActions,
              [groupKey]: isBlock ? 'block' : 'approve',
            },
          }));
          removeEmailsFromStore(ids);
          pruneDigestEmails(ids);

          try {
            const results = await Promise.allSettled(
              Array.from(contexts.values()).map((context) =>
                performSenderBulkAction({
                  accountId: context.accountId,
                  folderId: context.folderId,
                  sender: descriptor,
                  action: isBlock ? 'block' : 'approve',
                })
              )
            );
            const rejectedCount = results.filter((r) => r.status === 'rejected').length;
            if (rejectedCount > 0) {
              const { restoreAfterFailedAction } = await import('./emailCommands');
              const verb = isBlock ? 'block' : 'approve';
              if (rejectedCount === results.length) {
                restoreAfterFailedAction(
                  ids.map((id) => ({ id, email: snapshotById.get(id) ?? null })),
                  { message: `Failed to ${verb} sender — restored` }
                );
              } else {
                // Partial: some accounts changed the contact, some didn't —
                // surface it and reload server truth.
                restoreAfterFailedAction(
                  ids.map((id) => ({ id, email: null })),
                  { message: `${rejectedCount} account(s) failed to ${verb} this sender — refreshing` }
                );
              }
            }
          } finally {
            get().endGroupAction(groupKey);
          }
          return;
        }
        default:
          return;
      }
    },

    handleDigestMessageVisible: (email) => {
      if (email.read) {
        return;
      }
      updateDigestReadState([email.id], true);
      if (!email.accountId || !email.folderId) {
        return;
      }
      // Per-message scroll-mark: the reader saw THIS digest card, not its
      // thread — engine 'messages', never the row wrapper's 'threadRows'
      // (N2: routing through it marked every loaded member read).
      void (async () => {
        const { executeEmailAction } = await import('./emailCommands');
        await executeEmailAction('markRead', { kind: 'messages', ids: [email.id] });
      })();
    },

    collectSenderEmailsForGroup: async (group, options) => {
      const descriptor = resolveSenderDescriptor(group);
      if (!descriptor) {
        return group.emails;
      }
      const contexts = resolveSenderGroupContexts(group);
      if (!contexts.size) {
        return group.emails;
      }
      const limitHint = options?.limit ?? group.totalMessages ?? group.emails.length;
      const fetchLimit = Math.max(limitHint, group.emails.length, 1000);
      try {
        const slices = await Promise.all(
          Array.from(contexts.values()).map(async (context) => {
            const result = await fetchSenderDigest({
              accountId: context.accountId,
              folderId: context.folderId,
              sender: descriptor,
              limit: fetchLimit,
            });
            return result.emails;
          })
        );
        const combined = new Map<string, Email>();
        group.emails.forEach((email) => {
          combined.set(email.id, applyAttachmentState(email));
        });
        slices.flat().forEach((email) => {
          combined.set(email.id, applyAttachmentState(email));
        });
        return mergeEmailCollections(
          [],
          Array.from(combined.values())
        );
      } catch (error) {
        console.error('Failed to collect sender emails', error);
        return group.emails;
      }
    },
    flushPendingMailboxReload: async () => {
      const pending = get().pendingMailboxReload;
      if (!pending) {
        return;
      }

      // Re-check ownership at DRAIN time, not just at enqueue: bootstrap flushes
      // on every emailsLoading falling edge and a sender-group load-more flips
      // that flag, so a reload queued while a group / digest / triage owns the
      // list would otherwise fire straight into it (the L1 clobber the routing
      // was built to prevent). Keep the entry queued — it drains on group /
      // triage exit, which flushes again once ownership clears.
      if (shouldDeferMailboxReload()) {
        return;
      }

      // Check if there's an active skip config that matches this reload
      const skipConfig = get().skipNextMailboxReloadFor;
      if (skipConfig) {
        const isExpired = skipConfig.expiresAt <= Date.now();
        const matches =
          skipConfig.accountId === pending.accountId &&
          skipConfig.folderId === pending.folderId;

        if (matches && !isExpired) {
          return; // Don't flush yet, keep the pending reload
        }

        // If expired, clear it
        if (isExpired) {
          set({ skipNextMailboxReloadFor: null });
        }
      }

      const expiresAt = Date.now() + SKIP_MAILBOX_RELOAD_WINDOW_MS;
      set({
        pendingMailboxReload: null,
        skipNextMailboxReloadFor: {
          accountId: pending.accountId,
          folderId: pending.folderId,
          expiresAt,
        },
      });
      try {
        await useEmailStore
          .getState()
          .loadEmails(pending.accountId, pending.folderId, pending.options);
      } catch (error) {
        console.error('Failed to process pending mailbox reload', error);
      } finally {
        const current = get().skipNextMailboxReloadFor;
        if (current && current.expiresAt <= Date.now()) {
          set({ skipNextMailboxReloadFor: null });
        }
      }
    },
    updateDigestEmail,
    prefetchSenderGroup,
    pruneDigestEmails,
    setDigestReadState: updateDigestReadState,
    restoreDigestReadState,

    beginGroupAction: (groupKey, type) => {
      const folderId = useFolderStore.getState().selectedFolderId();
      const accountId = useAccountStore.getState().selectedAccountId;
      // Arm the reload-skip window and the in-flight marker in ONE update so
      // shouldDeferMailboxReload() is true before any API call can complete.
      set((state) => ({
        ...(accountId && folderId
          ? {
              skipNextMailboxReloadFor: {
                accountId,
                folderId,
                expiresAt: Date.now() + 5000,
              },
            }
          : {}),
        pendingGroupActions: {
          ...state.pendingGroupActions,
          [groupKey]: type,
        },
      }));
    },

    endGroupAction: (groupKey) => {
      set((state) => {
        const next = { ...state.pendingGroupActions };
        delete next[groupKey];
        // When no group actions remain, clear the pending reload WITHOUT
        // triggering it — the optimistic removal already settled the list and a
        // reload could resurrect mail the server hasn't finished moving.
        const noPendingActions = Object.keys(next).length === 0;
        return {
          pendingGroupActions: next,
          ...(noPendingActions ? { pendingMailboxReload: null } : {}),
        };
      });
    },

    resetSenderGroupingState: (reason) => {
      if (import.meta.env.DEV && reason) {
        console.debug('[senderGrouping] reset:', reason);
      }
      // The briefing drill-down scope is the same class of state as the sender
      // group drill-down: any navigation that cancels one must cancel the
      // other, or a stale scope header/suppression survives the folder change.
      // (Dynamic import: static would close the cycle through emailStore.)
      void import('./briefingStore').then(({ useBriefingStore }) => {
        useBriefingStore.getState().clearScope();
      });
      get().senderGroupLoadAbortController?.abort();
      get().senderDigestAbortController?.abort();
      get().senderDigestLoadMoreAbortController?.abort();
      set((state) => ({
        activeSenderGroup: null,
        activeSenderGroupInfo: null,
        activeSenderHydration: null,
        senderGroupLoadingMore: false,
        senderGroupContextPaging: {},
        senderDigest: null,
        senderDigestLoading: false,
        senderDigestLoadingMore: false,
        senderDigestVisible: false,
        senderGroupLoadAbortController: null,
        senderDigestAbortController: null,
        senderDigestLoadMoreAbortController: null,
        pendingGroupActions: {},
        skipNextMailboxReloadFor: null,
        pendingMailboxReload: null,
        senderDigestRequestId: state.senderDigestRequestId + 1,
      }));
    },
    };
  }, {
    name: 'sender-grouping-storage',
    version: 2,
    partialize: (state) => ({
      inboxMode: state.inboxMode,
    }),
    migrate: (persisted: unknown, version: number) => {
      if (version === 0) {
        const legacy = persisted as { groupBySender?: boolean } | null;
        return { inboxMode: legacy?.groupBySender === false ? 'flat' : 'senders' };
      }
      // v1 → v2: 'briefing' left the mode toggle (it's a sub-view tab now).
      const state = persisted as { inboxMode?: string } | null;
      if (state?.inboxMode !== 'flat' && state?.inboxMode !== 'senders') {
        return { inboxMode: 'senders' };
      }
      return persisted as { inboxMode: InboxMode };
    },
    // Keep the derived groupBySender mirror consistent with the rehydrated
    // inboxMode (partialize doesn't persist derived fields).
    merge: (persisted, current) => {
      const next = { ...current, ...(persisted as Partial<SenderGroupingStoreState>) };
      next.groupBySender = next.inboxMode === 'senders';
      return next;
    },
  }),
    { name: 'SenderGroupingStore' }
  )
);
